import { useEffect, useRef, useState } from "react"
import { useTranslation } from "next-i18next"
import { useDispatch } from "react-redux"
import useWindowResize from "@/hooks/useWindowResize"
import StickyHead from "@/components/ProductDetail/v3/ProductDetailView/StickyHead"
import MultiFinshProductBundleUI from "@/components/MultiFinshProductBundle/ui/MultiFinshProductBundleUI"
import { useStore } from "./context/store"
import {
  getProductInventory,
  getProductsBySku,
} from "@/services/productServices"
import { getProductInfo } from "@/components/MultiFinshProductBundle/analytics/getProductInfo"
import { getUserPersona, isShowAnalyticsEventLoaded } from "@/utils/helper"
import CONSTANTS, { SWATCH_NA } from "@/constants/index"
import {
  getCommonColorDetails,
  getSelectedProductPrices,
  getAddToCartItems,
  getStickyHeaderProps,
  processImageData,
} from "@/components/MultiFinshProductBundle/utils"
import {
  getSelectedColorImage,
  isAvailableInventory,
} from "@/responseProcessors/productResponseProcessor"

import { setPageObject, showPreviewCart } from "@/store/features/genericSlice"
import {
  loadCmpShowEvent,
  colorSelectorAnalytics,
  productOptionSelectorAnalytics,
  bundleAddToCartFromStickyAnalytics,
} from "@/components/MultiFinshProductBundle/analytics"
import {
  setInitSelectedProductsAction,
  setInventoryDataAction,
  setInventoryStatusAction,
  setProjectionDataAction,
  setSwatchColorAction,
} from "@/components/MultiFinshProductBundle/context/actions"
import { shareaddToCart } from "@/components/sharedCart/shareCartHelper"
import { getTranslations } from "@/components/Bundles/localization"

const MultiFinshProductBundle = props => {
  const { config } = props || {}
  const { state, dispatch } = useStore()
  const dispatchReduxAction = useDispatch()
  const {
    general: { siteName = "kohler" } = {},
    internationalization: { currencyCode = "USD", currencySign = "$" } = {},
  } = config

  const {
    swatchName: changedSwatchName = "",
    bundleItems = [],
    selectedProducts: selectedProductItems = [],
    pdpDetails: { productLocalCategory: category = "", sku = "" } = {},
    inventory = [],
    projectionData,
    imageGallery: { bundleIMageURL = "", bundleListImages = [] } = {},
  } = state

  const [skusPrice, setSkusPrice] = useState([])
  const [finalProductPrice, setFinalProductPrice] = useState(0)
  const [isPriceLoading, setIsPriceLoading] = useState(true)
  const [showStickyHeader, setShowStickyHeader] = useState(false)
  const addCartRef = useRef(null)
  const [width] = useWindowResize()
  const { t } = useTranslation("common")
  const staticTexts = getTranslations(t)
  const [productInfoBundle, setProductInfoBundle] = useState([])
  const [triggerAnalyticsOnColorChange, setTriggerAnalyticsOnColorChange] =
    useState(false)
  const [triggerAnalyticsOnProductClick, setTriggerAnalyticsOnProductClick] =
    useState(null)
  const skuPriceString = JSON.stringify(skusPrice)
  const productInfoBundleString = JSON.stringify(productInfoBundle)

  const commonColorsDefault = getCommonColorDetails({
    bundleItems,
  })

  const initSelectedProducts = bundleItems.map((bundle, index) => {
    const { name: defaultSwatchName = "" } = commonColorsDefault.at(0) || {}
    const { products = [], name = "" } = bundle || {}
    const { masterSku, sku, colorDetails = [] } = products.at(0)
    const { customerFacingSku = "" } =
      colorDetails.find(item => item.sku === masterSku) || {}

    return {
      id: index,
      masterSku,
      name,
      sku,
      swatchName: defaultSwatchName,
      customerFacingSku,
    }
  })

  const selectedProducts =
    selectedProductItems.length > 0
      ? selectedProductItems
      : initSelectedProducts

  const selectedSkus = selectedProducts.map(item => item?.sku)
  const selectedMasterSkus = selectedProducts.map(item => item?.masterSku)

  const commonColors = getCommonColorDetails({
    bundleItems,
    selectedMasterSkus,
  })

  const { name: swatchNameInit = "" } = commonColors.at(0) || {}
  const swatchName = changedSwatchName || swatchNameInit

  const inventoryString = JSON.stringify(inventory)
  const selectedProductsString = JSON.stringify(selectedProducts)
  const selectedSkusString = JSON.stringify(selectedSkus)

  const getAllBundleProductsVariantQuantity = async allBundleProductsSku => {
    dispatch(setInventoryStatusAction(false))
    const { inventories = [] } = await getProductInventory(
      allBundleProductsSku.join(",")
    )
    dispatch(setInventoryDataAction(inventories))
    dispatch(setInventoryStatusAction(true))
  }

  const getAllProductsPrice = async skus => {
    setIsPriceLoading(true)
    const productLists = await getProductsBySku(skus)
    dispatch(setProjectionDataAction(productLists))
    setIsPriceLoading(false)
  }

  const getAvailableColors = data => {
    const { swatchName, commonColors } = data || {}
    return commonColors.map((color, index) => {
      const { name = "", swatch = "" } = color
      return {
        id: swatch,
        name: "product-color",
        value: swatch,
        label: name,
        notApplicable: swatch.toLowerCase() === SWATCH_NA.toLowerCase(),
        colorUrl: CONSTANTS.COLOR_FILE_URL + swatch,
        checked: swatchName === name ? true : index === 0,
      }
    })
  }

  const avaiableColors = getAvailableColors({ commonColors, swatchName })
  const bundleStickyHeaderProps = getStickyHeaderProps(
    state,
    staticTexts,
    avaiableColors,
    swatchName
  )

  const { allImages, imageURL } = processImageData(
    bundleListImages,
    selectedSkus,
    bundleIMageURL
  )

  const stickyImageURL = imageURL.includes("<<IMAGE")
    ? allImages?.[0]?.url
    : imageURL

  const callOnColorChange = color => {
    const allskus = bundleItems?.map(bundle => {
      return bundle?.products?.map(product => {
        const { colorDetails, imageDetails } = product || {}
        const { sku: productSku } = getSelectedColorImage({
          colorDetails,
          imageDetails,
          swatchName: color,
        })

        return productSku
      })
    })
    const allBundleProductsSku = allskus?.flat() || []

    // Call the API to get the inventory and price data based on the selected swatch name
    if (allBundleProductsSku.length > 0) {
      getAllProductsPrice(allBundleProductsSku)
      getAllBundleProductsVariantQuantity(allBundleProductsSku)
    }
  }

  const handleClickSwatch = swatch => {
    dispatch(setSwatchColorAction(swatch))
    callOnColorChange(swatch)

    const productsData = []
    selectedProducts.forEach((item, index) => {
      const { masterSku, name = "" } = item || {}

      const { products = [] } =
        bundleItems.find(bundle => bundle.name === item.name) || {}

      const { colorDetails = [] } =
        products.find(product => product.masterSku === masterSku) || {}

      const { sku: variantSku = "", customerFacingSku = "" } =
        colorDetails.find(variant => variant.name === swatch) || {}

      productsData.push({
        id: index,
        name,
        masterSku,
        sku: variantSku,
        customerFacingSku,
        swatchName: swatch,
      })
    })

    dispatch(setInitSelectedProductsAction(productsData))
    setTriggerAnalyticsOnColorChange(true)
  }

  useEffect(() => {
    if (swatchNameInit) {
      callOnColorChange(swatchNameInit)
      const initSelectedProducts = bundleItems.map((bundle, index) => {
        const { products = [], name = "" } = bundle || {}
        const { masterSku, sku, colorDetails = [] } = products.at(0)
        const { customerFacingSku = "" } =
          colorDetails.find(item => item.sku === masterSku) || {}

        const isInventory = isAvailableInventory({
          inventory,
          sku: masterSku,
        })

        return {
          masterSku,
          index,
          name,
          sku,
          swatchName: swatchNameInit,
          isInventory,
          customerFacingSku,
        }
      })

      dispatch(setInitSelectedProductsAction(initSelectedProducts))
    }
  }, [swatchNameInit])

  // productInfo for bundle
  useEffect(() => {
    const productInfodata = getProductInfo({
      data: state,
      skusPrice: skusPrice,
      siteName: siteName,
      swatchName: swatchName,
    })
    setProductInfoBundle(productInfodata)
  }, [skuPriceString])

  // Set the productInfo in the global window object and dispatch it to the store
  // This is used to add the product info in the PDP initial datalayer events
  useEffect(() => {
    if (triggerAnalyticsOnColorChange) {
      colorSelectorAnalytics(productInfoBundle)
      setTriggerAnalyticsOnColorChange(false)
    }
    if (triggerAnalyticsOnProductClick) {
      let { sku: clickedSku = "", optionCategory = "" } =
        triggerAnalyticsOnProductClick || {}
      clickedSku = `K-${clickedSku}`
      const clickedProductInfo = productInfoBundle?.find(
        product => product?.productID === clickedSku
      )
      productOptionSelectorAnalytics(clickedProductInfo, optionCategory)
      setTriggerAnalyticsOnProductClick(null)
    }
    let cmpShowAnlyticsTimer = null
    if (!isShowAnalyticsEventLoaded() && productInfoBundle?.length) {
      window.productInfo = JSON.stringify(productInfoBundle)
      window.pageObj.productInfo = [...productInfoBundle]
      dispatch(setPageObject(true))
      const dataLayer = window.adobeDataLayer || []
      const loadTargetEvent = dataLayer.find(
        item => item?.event === "cmp:loadTarget"
      )
      if (loadTargetEvent) {
        loadTargetEvent.page = {
          ...(loadTargetEvent.page || window.pageObj),
          productInfo: [...productInfoBundle],
        }
      }

      // To trigger the cmp:show event
      cmpShowAnlyticsTimer = setInterval(() => {
        if (window.datalayerTopLoaded && !isShowAnalyticsEventLoaded()) {
          loadCmpShowEvent({
            productInfoBundle,
            siteName,
            category,
            sku,
            cmpShowAnlyticsTimer,
          })
        } else if (isShowAnalyticsEventLoaded()) {
          clearInterval(cmpShowAnlyticsTimer)
        }
      }, 100)
    }

    return () => {
      if (cmpShowAnlyticsTimer) {
        clearInterval(cmpShowAnlyticsTimer)
      }
      delete window.productInfo
    }
  }, [productInfoBundleString])

  const addCartCallback = entries => {
    const [entry] = entries
    const currentValue =
      !entry?.isIntersecting && entry?.boundingClientRect?.y < 0
    setShowStickyHeader(currentValue)
  }
  const options = { root: null, rootMargin: "0px", threshold: 1.0 }

  useEffect(() => {
    if (width > 1023) {
      const observer = new IntersectionObserver(addCartCallback, options)
      if (addCartRef?.current) observer.observe(addCartRef.current)
      return () => {
        if (addCartRef?.current) observer.observe(addCartRef.current)
      }
    }
  }, [addCartRef, options])

  const handleStickyNavAddToCart = (cb = () => {}) => {
    bundleAddToCartFromStickyAnalytics(productInfoBundle)

    const items = getAddToCartItems(
      selectedProducts,
      bundleItems,
      projectionData,
      1
    )
    shareaddToCart(items, "", isFaild => {
      !isFaild && dispatchReduxAction(showPreviewCart())
      cb()
    })
  }

  const projectionDataString = JSON.stringify(projectionData)

  useEffect(() => {
    const persona = getUserPersona()
    const productsPrice = getSelectedProductPrices({
      productLists: projectionData,
      skus: selectedSkus,
      currencyCode,
      persona,
    })

    const totalPrice = productsPrice.reduce(
      (sum, num) =>
        sum + (Number(num.discountPrice) || Number(num.priceProduct) || 0),
      0
    )
    setSkusPrice(productsPrice)
    setFinalProductPrice(totalPrice)
  }, [projectionDataString, selectedSkusString])

  useEffect(() => {
    const allBundleProducts = selectedProducts.map(item => {
      const isInventory = isAvailableInventory({
        inventory: inventory,
        sku: item.sku,
      })
      return { ...item, isInventory }
    })

    dispatch(setInitSelectedProductsAction(allBundleProducts))
  }, [selectedProductsString, inventoryString])

  return (
    <>
      <StickyHead
        showStickyHeader={showStickyHeader}
        price={finalProductPrice}
        currencySign={currencySign}
        isDynamicBundle={true}
        onAddToCart={handleStickyNavAddToCart}
        stickyHeaderImageURL={stickyImageURL}
        {...bundleStickyHeaderProps}
      />
      <MultiFinshProductBundleUI
        {...props}
        isPriceLoading={isPriceLoading}
        finalProductPrice={finalProductPrice}
        skusPrice={skusPrice}
        selectedProducts={selectedProducts}
        productInfoBundle={productInfoBundle}
        avaiableColors={avaiableColors}
        swatchName={swatchName}
        addCartRef={addCartRef}
        allImages={allImages}
        layeredIMageURL={imageURL}
        handleClickSwatch={handleClickSwatch}
        setTriggerAnalyticsOnProductClick={setTriggerAnalyticsOnProductClick}
      />
    </>
  )
}

export default MultiFinshProductBundle
